import { v4 as uuid } from 'uuid';
import BroadcastChannel from 'broadcast-channel';

export class BroadcastChannels {
    /** Event broadcasted when the device log request status changes*/
    public static readonly onDeviceLogRequestStatusChanged: string = 'onDeviceLogRequestStatusChanged';
    /** Event broadcasted when the video recording status changes*/
    public static readonly onVideoRecordingStatusChanged: string = 'onVideoRecordingStatusChanged';
    /** Event broadcasted when annotation status changes */
    public static readonly onAnnotationStatusChanged: string = 'onAnnotationStatusChanged';
    /** Event broadcasted when the saving snapshot state changes */
    public static readonly onDeviceSnapshotStatusChanged: string = 'onDeviceSnapshotStatusChanged';
    /** Event broadcasted when the saving snapshot state changes */
    public static readonly onDeviceCheckinStatusChanged: string = 'onDeviceCheckinStatusChanged';
    /** Event broadcasted when a new ticket session begins */
    public static readonly onTicketSessionChanged: string = 'onTicketSessionChanged';
    /** Event broadcasted when a snapshot rest request has been sent or response has been recieved that requires locking out user input */
    public static readonly onDeviceSnapshotRequestInProgress: string = 'onDeviceSnapshotLockoutStatusChanged';
    /** Event broadcasted when an rc menubar tab is changed */
    public static readonly onSnapshotViewVisibilityChanged: string = 'onSnapshotViewVisibilityChanged';

    public static readonly onDeviceTicketDataChanged: string = 'onDeviceTicketDataChanged';
    public static readonly onPopoutRemoteControlStart: string = 'onPopoutRemoteControlStart';
    public static readonly onDeviceStateSynchronizeChanged: string = 'onDeviceStateSynchronizeChanged';
    public static readonly onPopoutRemoteControlStop: string = 'onPopoutRemoteControlStop';
    public static readonly onPopoutRemoteControlStopped: string = 'onPopoutRemoteControlStopped';
    public static readonly onRemoteControlError: string = 'onRemoteControlError';
    public static readonly onWorkPanelPopout: string = 'onWorkPanelPopout';
    // When we start RC from popOut 'rcSessionEstablished' from mcrc-com-Session only comes to popOut remoteControlService.
    // To start remoteControlActivity correctly we should send a message to the remoteControlService main window.
    public static readonly onRemoteControlSessionEstablished: string = 'onRemoteControlSessionEstablished';

    public static readonly onRemoteControlEndedMessageForPopOut: string = 'onRemoteControlEndedMessageForPopOut';
}

declare type OnMessageHandler<T> = ((this: BroadcastService<T>, ev: T) => any) | null;

declare interface IMessagePayload<T> {
    sessionId: string;
    payload: T;
}

/**
 * This service will send strongly typed messages to anyone subscribed to the same string (received as a contructor parameter),
 * no matter what browser-tab or nodejs-processes they are in
 *
 * This is a wrapper of library broadcast-channel.
 * To broadcast a message:
 *      let sender: BroadcastService<YourMessageType> = new BroadcastService(BroadcastChannels.stringThatLinksSendersAndReceivers]);
 *      sender.postMessage(this.yourMessage); // yourMessage type must be YourMessageType
 *
 * To Receive messages:
 *      let receiver: BroadcastService<YourMessageType> = new BroadcastService(BroadcastChannels.stringThatLinksSendersAndReceivers]);
 *      receiver.onMessage = this.handleMessagesReceived;
 *
 * MessageHandler Disposal: It is strongly recommended to dispose of your message handlers when they are no longer necessary.
 *      if(this.handleMessageRecieved) {
 *          this.handleMessageReceived.close();
 *      }
 *
 * Complete documentation can be found here: https://github.com/pubkey/broadcast-channel
 */
export class BroadcastService<T> {

    /** sessionId  is used to constrain the message broadcasting to a specific "user session"
     * User could be logged in in two windows, (two sessions) and wach window can have its own popups
     * Messages should flow only between windows under the same session.
     * If called without a uuid, it willl create one */
    public static sessionId: string = uuid();

    private _broadcastChannel: BroadcastChannel<IMessagePayload<T>>;
    private _onMessage: OnMessageHandler<T>;

    constructor(name: string) {
        this._broadcastChannel = new BroadcastChannel<IMessagePayload<T>>(name);
        this._broadcastChannel.onmessage = this._onBroadcastChannelMessage;
    }

    public set onMessage(onMessageHandler: OnMessageHandler<T>) {
        this._onMessage = onMessageHandler;
    }

    public postMessage = (msg: T): Promise<void> => {

        const payload = {
            sessionId: BroadcastService.sessionId,
            payload: msg
        } as IMessagePayload<T>;

        // We'll send anIMessagePayload with the msg as payload
        return this._broadcastChannel.postMessage(payload);
    };

    public close = (): Promise<void> => {

        this._onMessage = undefined;
        return this._broadcastChannel.close();
    };

    public get isClosed() {
        return (<any>this._broadcastChannel).closed ?? false;
    }

    private _onBroadcastChannelMessage = (message: IMessagePayload<T>): void => {

        if (message.sessionId !== BroadcastService.sessionId) {
            // This message is for a different session
            return;
        }

        if (this._onMessage) {
            this._onMessage(message.payload);
        }
    };
}
